import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { FieldCoordinates } from '../types/codeGeneration';

interface UploadResult {
  documentId: string;
  originalFilename: string;
  fileSize: number;
  status: string;
  // Identity of the session the backend auto-creates on upload
  // (AUTOMATIC_INITIAL_SAVE_POINT.md). Absent when auto-creation failed, and
  // absent on the synthetic upload results built when loading a saved session.
  sessionId?: string;
  sessionName?: string;
  // Non-fatal facts about the uploaded file, produced by the ingest pipeline and
  // reported in the upload response — today only that the document's links will not
  // survive generation (docs/design/WHAT_ACCESSIBLE_STILL_MEANS.md §1.4).
  //
  // Carried on the upload result rather than in its own state so it has the same
  // lifetime as the document it describes: CLEAR_SESSION resets it along with
  // everything else, and the synthetic result built when loading a saved session simply
  // has none — that path never re-reads the file, so it has nothing to report.
  warnings?: Array<{ code: string; message: string }>;
  // The accessibility report card the backend scan produces at upload
  // (docs/design/ACCESSIBILITY_AUDIT_ON_UPLOAD.md). Same lifetime as the warnings above
  // and for the same reason — it describes this uploaded document, and a session load,
  // which never re-reads the file, carries none. Typed loosely here (the context is the
  // state container, not the renderer); AccessibilityReportCard owns the precise shape.
  accessibilityReport?: any;
}

interface SessionState {
  // Current session info
  currentSessionId: string | null;
  sessionName: string;
  deploymentStatus: string;
  isEditingExistingSession: boolean;

  // Document and fields
  uploadResult: UploadResult;
  selectedFields: FieldCoordinates[];
  savedFieldsSnapshot: FieldCoordinates[]; // Snapshot of fields when session was last saved/loaded

  // UI state
  hasUnsavedChanges: boolean;
  currentPage: number;
  scale: number;

  // Session management
  availableSessions: any[];
  isLoadingSessions: boolean;
}

// Actions
type SessionAction =
  | { type: 'SET_CURRENT_SESSION'; payload: { sessionId: string; sessionName: string; deploymentStatus?: string } }
  | { type: 'SET_UPLOAD_RESULT'; payload: UploadResult }
  | { type: 'SET_SELECTED_FIELDS'; payload: FieldCoordinates[] }
  | { type: 'ADD_FIELD'; payload: FieldCoordinates }
  | { type: 'UPDATE_FIELD'; payload: FieldCoordinates }
  | { type: 'DELETE_FIELD'; payload: string }
  | { type: 'REORDER_FIELDS'; payload: FieldCoordinates[] }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean }
  | { type: 'SAVE_SUCCEEDED' }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_SCALE'; payload: number }
  | { type: 'SET_AVAILABLE_SESSIONS'; payload: any[] }
  | { type: 'SET_LOADING_SESSIONS'; payload: boolean }
  | { type: 'LOAD_SESSION'; payload: any }
  | { type: 'CLEAR_SESSION' }
  | { type: 'SET_SESSION_NAME'; payload: string }
  | { type: 'SET_DEPLOYMENT_STATUS'; payload: string }
  | { type: 'SET_EDITING_EXISTING'; payload: boolean };

// Empty upload result for initial state
const emptyUploadResult: UploadResult = {
  documentId: '',
  originalFilename: '',
  fileSize: 0,
  status: 'none'
};

// Initial state
/**
 * Exported for `__tests__/fieldIdentity.test.ts`, which drives the reducer directly.
 *
 * The duplicate-id defect of 2 October 2026 was a pure state bug — a list holding one id
 * twice — and it reached a tester because nothing asserted field identity at this layer.
 * A reducer is a pure function of (state, action); testing it through a rendered provider
 * would have obscured exactly what went wrong.
 */
export const initialSessionState: SessionState = {
  currentSessionId: null,
  sessionName: '',
  deploymentStatus: 'planning',
  isEditingExistingSession: false,
  uploadResult: emptyUploadResult,
  selectedFields: [],
  savedFieldsSnapshot: [],
  hasUnsavedChanges: false,
  currentPage: 1,
  scale: 0.5,
  availableSessions: [],
  isLoadingSessions: false,
};

// Helper function to compare field arrays
/**
 * Drop entries that repeat an id already seen, keeping the first.
 *
 * A LIST THAT CONTAINS THE SAME ID TWICE BREAKS EVERY OPERATION THAT ADDRESSES A FIELD.
 * Measured in QA, 2 October 2026, on a session that had carried two identically named
 * fields through a save:
 *
 *   - UPDATE_FIELD matches on id, so a rename changed BOTH entries at once
 *   - FieldListSidebar keys on field.id, and React collapses duplicate keys, so the list
 *     showed two fields while the page showed three
 *   - deleting one left the other drawn on the page with no properties to open
 *
 * The page was the honest view: the array really did hold three entries. So the repair
 * belongs in the data, not in the drawing.
 *
 * HOW THE DUPLICATE ARRIVES, which is why this is a load-time repair rather than a guard
 * against bad callers: field_configurations upserts on (document_id, field_name), so two
 * same-named fields write to ONE row. That row's id is then stored twice in the session's
 * field_configuration_ids, and the load resolves both to the same row. The duplicate is
 * already in the database by the time this code runs, and refusing to load the session
 * would leave the author with no way back to their work.
 *
 * Keeping the FIRST is safe precisely because the twins came from one row: they are equal.
 * Logged rather than silent, because a session quietly losing an entry is the kind of thing
 * that should leave a trace someone can find later.
 */
function dedupeFieldsById(fields: FieldCoordinates[]): FieldCoordinates[] {
  const seen = new Set<string>();
  const unique = fields.filter((field) => {
    if (seen.has(field.id)) {
      return false;
    }
    seen.add(field.id);
    return true;
  });
  if (unique.length !== fields.length) {
    console.warn(
      `SessionContext: dropped ${fields.length - unique.length} field entr(ies) sharing an id ` +
        `already present. A session that stored two identically named fields produces this; ` +
        `the duplicate is repaired here so rename, delete and selection address one field each.`
    );
  }
  return unique;
}

function fieldsAreEqual(fields1: FieldCoordinates[], fields2: FieldCoordinates[]): boolean {
  if (fields1.length !== fields2.length) {
    return false;
  }

  // Sort both arrays by field ID for consistent comparison
  const sorted1 = [...fields1].sort((a, b) => a.id.localeCompare(b.id));
  const sorted2 = [...fields2].sort((a, b) => a.id.localeCompare(b.id));

  for (let i = 0; i < sorted1.length; i++) {
    if (!singleFieldEquals(sorted1[i], sorted2[i])) {
      return false;
    }
  }

  return true;
}

// Deep-compare two fields across EVERY authored, persisted property. This is the
// change-detector behind hasUnsavedChanges, and project generation is built from the
// SAVED session — so any authored property omitted here produces the same silent bug:
// an edit to ONLY that property leaves the session looking "saved", the author generates,
// and the project is built from the pre-edit snapshot. (dataKey was the first instance
// found in the field; the fuller list below closes the rest of the same class, several of
// them accessibility-critical — label, readOnly, the image-region and paragraph configs.)
// The live preview and fillable PDF run off in-memory state, so they never reveal the gap.
export function singleFieldEquals(f1: FieldCoordinates, f2: FieldCoordinates): boolean {
  if (f1.id !== f2.id ||
      f1.name !== f2.name ||
      f1.type !== f2.type ||
      f1.x !== f2.x ||
      f1.y !== f2.y ||
      f1.width !== f2.width ||
      f1.height !== f2.height ||
      f1.pageNumber !== f2.pageNumber ||
      (f1.defaultValue ?? '') !== (f2.defaultValue ?? '') ||
      (f1.dataKey ?? '') !== (f2.dataKey ?? '') ||
      (f1.label ?? '') !== (f2.label ?? '') ||
      (f1.formatHint ?? '') !== (f2.formatHint ?? '') ||
      Boolean(f1.required) !== Boolean(f2.required) ||
      Boolean(f1.readOnly) !== Boolean(f2.readOnly) ||
      (f1.fontSize ?? null) !== (f2.fontSize ?? null) ||
      (f1.textAlign ?? '') !== (f2.textAlign ?? '')) {
    return false;
  }

  return jsonEquals(f1.radioGroupConfig, f2.radioGroupConfig) &&
         jsonEquals(f1.characterBlockConfig, f2.characterBlockConfig) &&
         jsonEquals(f1.selectConfig, f2.selectConfig) &&
         jsonEquals(f1.pushButtonConfig, f2.pushButtonConfig) &&
         jsonEquals(f1.imageRegionConfig, f2.imageRegionConfig) &&
         jsonEquals(f1.paragraphConfig, f2.paragraphConfig);
}

function jsonEquals(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// Reducer
export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'SET_CURRENT_SESSION':
      return {
        ...state,
        currentSessionId: action.payload.sessionId,
        sessionName: action.payload.sessionName,
        deploymentStatus: action.payload.deploymentStatus || 'planning',
        isEditingExistingSession: true,
        hasUnsavedChanges: false,
      };
      
    case 'SET_UPLOAD_RESULT':
      return {
        ...state,
        uploadResult: action.payload,
      };
      
    case 'SET_SELECTED_FIELDS': {
      // Deduped before anything else looks at it — see dedupeFieldsById. The snapshot gets
      // the same list, so a repaired session does not load already claiming unsaved
      // changes; the stored duplicate heals the next time the author saves for any reason.
      const incomingFields = dedupeFieldsById(action.payload);
      // When setting fields (typically after loading a session), create a snapshot
      // and compare with current snapshot to determine if there are unsaved changes
      const isLoadingSession = state.savedFieldsSnapshot.length === 0 ||
                               !fieldsAreEqual(incomingFields, state.selectedFields);
      return {
        ...state,
        selectedFields: incomingFields,
        savedFieldsSnapshot: isLoadingSession ? [...incomingFields] : state.savedFieldsSnapshot,
        hasUnsavedChanges: !fieldsAreEqual(incomingFields, state.savedFieldsSnapshot),
      };
    }
      
    case 'ADD_FIELD':
      const newFields = [...state.selectedFields, action.payload];
      return {
        ...state,
        selectedFields: newFields,
        hasUnsavedChanges: !fieldsAreEqual(newFields, state.savedFieldsSnapshot),
      };

    case 'UPDATE_FIELD':
      const updatedFields = state.selectedFields.map(field =>
        field.id === action.payload.id ? action.payload : field
      );
      return {
        ...state,
        selectedFields: updatedFields,
        hasUnsavedChanges: !fieldsAreEqual(updatedFields, state.savedFieldsSnapshot),
      };

    case 'DELETE_FIELD':
      const filteredFields = state.selectedFields.filter(field => field.id !== action.payload);
      return {
        ...state,
        selectedFields: filteredFields,
        hasUnsavedChanges: !fieldsAreEqual(filteredFields, state.savedFieldsSnapshot),
      };

    case 'REORDER_FIELDS':
      // hasUnsavedChanges is set OUTRIGHT, not computed: fieldsAreEqual sorts both
      // sides by id before comparing, so it cannot see a reorder at all. That is
      // right for what it was written for (did a load change anything?) and wrong
      // here, where the ORDER is the change. Leaving it computed would let an
      // author set a reading order, navigate away with no warning, and lose it.
      return {
        ...state,
        selectedFields: action.payload,
        hasUnsavedChanges: true,
      };
      
    case 'SET_UNSAVED_CHANGES':
      return {
        ...state,
        hasUnsavedChanges: action.payload,
      };

    case 'SAVE_SUCCEEDED':
      // Refresh the saved snapshot so subsequent equality checks compare against
      // what was actually persisted. SET_SELECTED_FIELDS can't do this — its
      // payload-vs-current-state comparison treats a no-op resave as "unchanged"
      // and leaves the snapshot at the pre-load baseline.
      return {
        ...state,
        savedFieldsSnapshot: [...state.selectedFields],
        hasUnsavedChanges: false,
      };
      
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
      
    case 'SET_SCALE':
      return {
        ...state,
        scale: action.payload,
      };
      
    case 'SET_AVAILABLE_SESSIONS':
      return {
        ...state,
        availableSessions: action.payload,
      };
      
    case 'SET_LOADING_SESSIONS':
      return {
        ...state,
        isLoadingSessions: action.payload,
      };
      
    case 'LOAD_SESSION':
      const sessionData = action.payload;
      // Deduped here too: this is the other way a stored session reaches state, and the
      // duplicate arrives from the database rather than from anything the author did.
      const loadedFields = dedupeFieldsById(sessionData.selectedFields || []);
      return {
        ...state,
        currentSessionId: sessionData.sessionId,
        sessionName: sessionData.sessionName || '',
        deploymentStatus: sessionData.deploymentStatus || 'planning',
        isEditingExistingSession: true,
        uploadResult: {
          documentId: sessionData.documentId,
          originalFilename: sessionData.originalFilename,
          fileSize: 0,
          status: 'success',
        },
        selectedFields: loadedFields,
        savedFieldsSnapshot: [...loadedFields],
        currentPage: (sessionData.currentPage && sessionData.currentPage > 0) ? sessionData.currentPage : 1,
        scale: (sessionData.scale && sessionData.scale > 0) ? sessionData.scale : 0.5,
        hasUnsavedChanges: false,
      };
      
    case 'CLEAR_SESSION':
      return {
        ...state,
        currentSessionId: null,
        sessionName: '',
        deploymentStatus: 'planning',
        isEditingExistingSession: false,
        uploadResult: emptyUploadResult,
        selectedFields: [],
        savedFieldsSnapshot: [],
        hasUnsavedChanges: false,
        currentPage: 1,
        scale: 0.5,
        // Wipe the cached session list too. Otherwise after a license switch
        // or sign-out, the Load dialog briefly shows the prior tenant's
        // session names before the new license's sessions get refetched.
        availableSessions: [],
        isLoadingSessions: false,
      };
      
    case 'SET_SESSION_NAME':
      return {
        ...state,
        sessionName: action.payload,
      };
      
    case 'SET_DEPLOYMENT_STATUS':
      return {
        ...state,
        deploymentStatus: action.payload,
      };
      
    case 'SET_EDITING_EXISTING':
      return {
        ...state,
        isEditingExistingSession: action.payload,
      };
      
    default:
      return state;
  }
}

// Context
const SessionContext = createContext<{
  state: SessionState;
  dispatch: React.Dispatch<SessionAction>;
} | null>(null);

// Provider component
export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState);

  return (
    <SessionContext.Provider value={{ state, dispatch }}>
      {children}
    </SessionContext.Provider>
  );
};

// Custom hook
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

// Action creators for convenience
export const sessionActions = {
  setCurrentSession: (sessionId: string, sessionName: string, deploymentStatus?: string) => ({
    type: 'SET_CURRENT_SESSION' as const,
    payload: { sessionId, sessionName, deploymentStatus },
  }),
  
  setUploadResult: (uploadResult: UploadResult) => ({
    type: 'SET_UPLOAD_RESULT' as const,
    payload: uploadResult,
  }),
  
  setSelectedFields: (fields: FieldCoordinates[]) => ({
    type: 'SET_SELECTED_FIELDS' as const,
    payload: fields,
  }),
  
  addField: (field: FieldCoordinates) => ({
    type: 'ADD_FIELD' as const,
    payload: field,
  }),
  
  updateField: (field: FieldCoordinates) => ({
    type: 'UPDATE_FIELD' as const,
    payload: field,
  }),
  
  deleteField: (fieldId: string) => ({
    type: 'DELETE_FIELD' as const,
    payload: fieldId,
  }),

  /** Replace the field array with a new ORDER of the same fields (D37). */
  reorderFields: (fields: FieldCoordinates[]) => ({
    type: 'REORDER_FIELDS' as const,
    payload: fields,
  }),
  
  setUnsavedChanges: (hasChanges: boolean) => ({
    type: 'SET_UNSAVED_CHANGES' as const,
    payload: hasChanges,
  }),

  saveSucceeded: () => ({
    type: 'SAVE_SUCCEEDED' as const,
  }),
  
  loadSession: (sessionData: any) => ({
    type: 'LOAD_SESSION' as const,
    payload: sessionData,
  }),
  
  clearSession: () => ({
    type: 'CLEAR_SESSION' as const,
  }),
};